import { useState, useEffect } from 'react';
import { Paper, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Radio, CircularProgress, Alert, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import UploadModal from './UploadModal';
import ModifyCalendarModal from './ModifyCalendarModal';
import { getCalendarData, deleteCalendar } from '../../services/adminApiClient';

export default function CalendarsTab({ customers, loading: customersLoading, error: customersError }) {
    const [countryCode, setCountryCode] = useState('US'); // NEW
    const [allCalendarData, setAllCalendarData] = useState({});
    const [displayedData, setDisplayedData] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [isUploadModalOpen, setUploadModalOpen] = useState(false);
    const [isModifyModalOpen, setModifyModalOpen] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState('');
    const [actionStatus, setActionStatus] = useState({ type: '', text: '' });

    const fetchData = async () => {
        setLoadingData(true);
        setError('');
        try {
            const response = await getCalendarData();
            if (response.success && Array.isArray(response.data)) {
                const groupedData = response.data.reduce((acc, item) => {
                    const key = item.client_code;
                    if (!acc[key]) acc[key] = [];
                    acc[key].push({
                        id: item.calendar_id,
                        year: item.calendar_year,
                        clientName: item.client_name,
                        clientCode: item.client_code,
                        fileName: item.file_name,
                        status: item.status,
                        timestamp: new Date(item.timestamp).toLocaleString(),
                        uploadedBy: item.uploaded_by,
                        lastUpdatedBy: item.last_updated_by
                    });
                    return acc;
                }, {});
                setAllCalendarData(groupedData);
            } else {
                throw new Error("Invalid data format received from API.");
            }
        } catch (e) {
            setError(e.message || "Failed to fetch calendar data.");
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    useEffect(() => {
        if (selectedCustomer && selectedCustomer.ClientCode) {
            setDisplayedData(allCalendarData[selectedCustomer.ClientCode] || []);
            setSelectedRowId(null);
        } else {
            setDisplayedData([]);
        }
    }, [selectedCustomer, allCalendarData]);

    const handleCustomerChange = (event) => {
        const customerCode = event.target.value;
        const customer = filteredCustomers.find(c => c.ClientCode === customerCode);
        setSelectedCustomer(customer);
    };

    const handleCountryChange = (event) => {
        const newCountry = event.target.value;
        setCountryCode(newCountry);
        setSelectedCustomer(null);
    };

    const handleDelete = async () => {
        if (!selectedRowId) return;
        const selectedCal = displayedData.find(d => d.id === selectedRowId);
        if (window.confirm(`Are you sure you want to delete "${selectedCal.fileName}"?`)) {
            try {
                await deleteCalendar({ calendar_id: selectedCal.id, file_name: selectedCal.fileName });
                setActionStatus({ type: 'success', text: 'Deleted successfully!' });
                fetchData();
            } catch (e) {
                setActionStatus({ type: 'error', text: e.message || 'Failed to delete.' });
            }
        }
    };

    const handleUploadSubmit = () => {
        setUploadModalOpen(false);
        fetchData();
        setActionStatus({ type: 'success', text: 'Uploaded successfully!' });
    };

    const handleModifySubmit = () => {
        setModifyModalOpen(false);
        fetchData();
        setActionStatus({ type: 'success', text: 'Updated successfully!' });
    };

    const filteredCustomers = customers.filter(c => c.region === countryCode);

    if (customersLoading) return <CircularProgress />;
    if (customersError) return <Alert severity="error">Error loading customers: {customersError}</Alert>;

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Calendars</Typography>
            {actionStatus.text && <Alert severity={actionStatus.type} sx={{ mb: 2 }}>{actionStatus.text}</Alert>}
            <Grid container spacing={2} alignItems="center">
                {/* Country Dropdown */}
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>COUNTRY</InputLabel>
                        <Select value={countryCode} label="COUNTRY" onChange={handleCountryChange}>
                            <MenuItem value="US">US</MenuItem>
                            <MenuItem value="CA">CA</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                {/* Customer Dropdown */}
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>CUSTOMERS</InputLabel>
                        <Select
                            value={selectedCustomer ? selectedCustomer.ClientCode : ''}
                            label="CUSTOMERS"
                            onChange={handleCustomerChange}
                        >
                            {filteredCustomers.map(customer => (
                                <MenuItem key={customer.ClientCode} value={customer.ClientCode}>
                                    {customer.ClientName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button variant="outlined" onClick={() => setUploadModalOpen(true)} disabled={!selectedCustomer}>Upload New</Button>
                    <Button variant="outlined" onClick={() => setModifyModalOpen(true)} disabled={!selectedRowId}>Modify</Button>
                    <Button variant="contained" color="error" onClick={handleDelete} disabled={!selectedRowId}>Delete</Button>
                </Grid>
            </Grid>

            {loadingData && <CircularProgress sx={{ mt: 2 }} />}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            {!loadingData && !error && (
                <TableContainer component={Paper} sx={{ mt: 3, border: '1px solid #ddd' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                                <TableCell padding="checkbox"></TableCell>
                                <TableCell>Calendar ID</TableCell>
                                <TableCell>Calendar Year</TableCell>
                                <TableCell>File Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Timestamp</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedData.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell padding="checkbox">
                                        <Radio checked={selectedRowId === row.id} onChange={() => setSelectedRowId(row.id)} value={row.id} />
                                    </TableCell>
                                    <TableCell>{row.id}</TableCell>
                                    <TableCell>{row.year}</TableCell>
                                    <TableCell>{row.fileName}</TableCell>
                                    <TableCell>{row.status}</TableCell>
                                    <TableCell>{row.timestamp}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <UploadModal
                open={isUploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                customer={selectedCustomer}
                onSubmitSuccess={handleUploadSubmit}
            />
            <ModifyCalendarModal
                open={isModifyModalOpen}
                onClose={() => setModifyModalOpen(false)}
                customer={selectedCustomer}
                calendarData={displayedData.find(d => d.id === selectedRowId)}
                onSubmitSuccess={handleModifySubmit}
            />
        </Paper>
    );
}
